import { and, asc, eq, isNull } from 'drizzle-orm';
import {
  budgetSettings,
  expectedMoney,
  expectedReceipts,
  funds,
  incomeSources,
  ledgerEntries,
  moneyAccounts,
  paydays,
  receivables,
} from '@tahi/db';
import {
  budgetSettingsId,
  DEFAULT_BUDGET_SETTINGS,
  isOtherIncome,
  sortAccounts,
  sortFunds,
  type AccountEntryLike,
  type AccountGrowth,
  type AccountKind,
  type AccountLike,
  type ReceiptEntryLike,
  type ReceivableLike,
  type AccountType,
  type ExpectedMoneyLike,
  type ExpectedReceiptLike,
  type ExpectedRepeat,
  type FundLike,
  type FundPeriod,
  type IncomeKind,
  type IncomeSchedule,
  type IncomeSourceLike,
  type PaydayRowLike,
  type PaydayStatus,
  type WeekendRule,
} from '@tahi/core';
import type { LocalDb } from '../localDb';

/** budget_settings as screens read it; `exists` is false until the row is on this device. */
export interface BudgetSettingsView {
  autoDeduct: boolean;
  defaultFundId: string | null;
  /** v0.5: "Use funds (spending envelopes)", off by default. */
  useFunds: boolean;
  /** v0.5: "Pay from" and "Receive into" (a deleted account reads as null). */
  defaultAccountId: string | null;
  incomeAccountId: string | null;
  exists: boolean;
}

/** A one-off income row Budget owns (no payday behind it), deleted ones included. */
export interface OtherIncomeEntry {
  id: string;
  amountMinor: number;
  date: string;
  note: string | null;
  accountId: string | null;
  deletedAt: string | null;
}

/** The income row if it is other income (isOtherIncome); null for paydays, expenses and unknown ids. */
export async function getOtherIncome(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<OtherIncomeEntry | null> {
  const r = (
    await db
      .select()
      .from(ledgerEntries)
      .where(and(eq(ledgerEntries.userId, userId), eq(ledgerEntries.id, id)))
      .limit(1)
  )[0];
  if (!r || !isOtherIncome(r)) return null;
  return {
    id: r.id,
    amountMinor: r.amountMinor,
    date: r.date,
    note: r.note ?? null,
    accountId: r.accountId ?? null,
    deletedAt: r.deletedAt ?? null,
  };
}

type FundRow = typeof funds.$inferSelect;
type SourceRow = typeof incomeSources.$inferSelect;
type PaydayRow = typeof paydays.$inferSelect;

/** JSON columns arrive parsed through Drizzle's json mode; a raw string is parsed here just in case. */
function json<T>(v: unknown, fallback: T): T {
  if (v == null) return fallback;
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as T;
    } catch {
      return fallback;
    }
  }
  return v as T;
}

function toFund(r: FundRow): FundLike {
  return {
    id: r.id,
    name: r.name,
    period: r.period as FundPeriod,
    amountMinor: r.amountMinor,
    startDate: r.startDate,
    refillFromIncomeSourceId: r.refillFromIncomeSourceId ?? null,
    position: r.position ?? 0,
    accountIds: json<string[]>(r.accountIds, []),
    deletedAt: r.deletedAt ?? null,
  };
}

function toSource(r: SourceRow): IncomeSourceLike {
  return {
    id: r.id,
    name: r.name,
    kind: r.kind as IncomeKind,
    schedule: json<IncomeSchedule>(r.schedule, { kind: 'semi_monthly', days: [15, 31] }),
    defaultAmounts: json<number[]>(r.defaultAmounts, []),
    weekendRule: r.weekendRule as WeekendRule,
    startDate: r.startDate,
    endDate: r.endDate ?? null,
    categoryId: r.categoryId ?? null,
    accountId: r.accountId ?? null,
    deletedAt: r.deletedAt ?? null,
  };
}

function toPaydayRow(r: PaydayRow): PaydayRowLike {
  return {
    id: r.id,
    sourceId: r.sourceId,
    scheduledDate: r.scheduledDate,
    actualDate: r.actualDate ?? null,
    defaultAmountMinor: r.defaultAmountMinor,
    amountMinor: r.amountMinor ?? null,
    status: r.status as PaydayStatus,
    ledgerEntryId: r.ledgerEntryId ?? null,
    deletedAt: r.deletedAt ?? null,
  };
}

/** Live funds, by position. */
export async function listFunds(db: LocalDb, userId: string): Promise<FundLike[]> {
  const rows = await db
    .select()
    .from(funds)
    .where(and(eq(funds.userId, userId), isNull(funds.deletedAt)));
  return sortFunds(rows.map(toFund));
}

/** One fund, including a soft-deleted one. */
export async function getFund(db: LocalDb, userId: string, id: string): Promise<FundLike | null> {
  const rows = await db
    .select()
    .from(funds)
    .where(and(eq(funds.userId, userId), eq(funds.id, id)))
    .limit(1);
  return rows[0] ? toFund(rows[0]) : null;
}

/** The settings row, or the spec defaults. A default fund that was deleted reads as null. */
export async function getBudgetSettingsView(
  db: LocalDb,
  userId: string,
): Promise<BudgetSettingsView> {
  const rows = await db
    .select()
    .from(budgetSettings)
    .where(eq(budgetSettings.id, budgetSettingsId(userId)))
    .limit(1);
  const r = rows[0];
  if (!r || r.deletedAt) return { ...DEFAULT_BUDGET_SETTINGS, exists: false };
  let defaultFundId = r.defaultFundId ?? null;
  if (defaultFundId) {
    const f = await getFund(db, userId, defaultFundId);
    if (!f || f.deletedAt) defaultFundId = null;
  }
  const liveAccount = async (id: string | null | undefined) => {
    if (!id) return null;
    const a = await getAccount(db, userId, id);
    return a && !a.deletedAt ? id : null;
  };
  return {
    autoDeduct: Boolean(r.autoDeduct),
    defaultFundId,
    useFunds: Boolean(r.useFunds),
    defaultAccountId: await liveAccount(r.defaultAccountId),
    incomeAccountId: await liveAccount(r.incomeAccountId),
    exists: true,
  };
}

/** In the order the user added them (the first salary sets the cutoffs). */
export async function listIncomeSources(
  db: LocalDb,
  userId: string,
  opts: { includeDeleted?: boolean } = {},
): Promise<IncomeSourceLike[]> {
  const where = opts.includeDeleted
    ? eq(incomeSources.userId, userId)
    : and(eq(incomeSources.userId, userId), isNull(incomeSources.deletedAt));
  const rows = await db
    .select()
    .from(incomeSources)
    .where(where)
    .orderBy(asc(incomeSources.createdAt), asc(incomeSources.id));
  return rows.map(toSource);
}

export async function getIncomeSource(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<IncomeSourceLike | null> {
  const rows = await db
    .select()
    .from(incomeSources)
    .where(and(eq(incomeSources.userId, userId), eq(incomeSources.id, id)))
    .limit(1);
  return rows[0] ? toSource(rows[0]) : null;
}

/** Live payday rows (only confirmed or skipped paydays are ever stored). */
export async function listPaydayRows(db: LocalDb, userId: string): Promise<PaydayRowLike[]> {
  const rows = await db
    .select()
    .from(paydays)
    .where(and(eq(paydays.userId, userId), isNull(paydays.deletedAt)));
  return rows.map(toPaydayRow);
}

// ---------- v0.5 Accounts ----------

type AccountRow = typeof moneyAccounts.$inferSelect;
type ExpectedRow = typeof expectedMoney.$inferSelect;

function toAccount(r: AccountRow): AccountLike {
  return {
    id: r.id,
    name: r.name,
    type: (r.type ?? null) as AccountType | null,
    openingBalanceMinor: r.openingBalanceMinor,
    openingDate: r.openingDate,
    position: r.position ?? 0,
    deletedAt: r.deletedAt ?? null,
    kind: (r.kind ?? 'spending') as AccountKind,
    useForSpending: r.useForSpending == null ? true : Boolean(r.useForSpending),
    annualRateBp: r.annualRateBp ?? null,
    growth: (r.growth ?? null) as AccountGrowth | null,
    startDate: r.startDate ?? null,
    maturityDate: r.maturityDate ?? null,
    locked: Boolean(r.locked),
  };
}

type ExpectedReceiptRow = typeof expectedReceipts.$inferSelect;

function toExpectedReceipt(r: ExpectedReceiptRow): ExpectedReceiptLike {
  return {
    id: r.id,
    expectedId: r.expectedId,
    occurrenceDate: r.occurrenceDate,
    status: r.status,
    receivedDate: r.receivedDate ?? null,
    amountMinor: r.amountMinor ?? null,
    accountId: r.accountId ?? null,
    ledgerEntryId: r.ledgerEntryId ?? null,
    deletedAt: r.deletedAt ?? null,
  };
}

function toExpected(r: ExpectedRow, receipts: ExpectedReceiptLike[] = []): ExpectedMoneyLike {
  return {
    id: r.id,
    name: r.name,
    amountMinor: r.amountMinor,
    expectedDate: r.expectedDate,
    accountId: r.accountId ?? null,
    status: r.status,
    receivedDate: r.receivedDate ?? null,
    receivedMinor: r.receivedMinor ?? null,
    ledgerEntryId: r.ledgerEntryId ?? null,
    deletedAt: r.deletedAt ?? null,
    repeat: (r.repeat ?? 'none') as ExpectedRepeat,
    repeatUntil: r.repeatUntil ?? null,
    receipts: receipts.filter((x) => x.expectedId === r.id),
  };
}

/** Live accounts in the user's order. */
export async function listAccounts(db: LocalDb, userId: string): Promise<AccountLike[]> {
  const rows = await db
    .select()
    .from(moneyAccounts)
    .where(and(eq(moneyAccounts.userId, userId), isNull(moneyAccounts.deletedAt)));
  return sortAccounts(rows.map(toAccount));
}

/** One account, including a soft-deleted one. */
export async function getAccount(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<AccountLike | null> {
  const rows = await db
    .select()
    .from(moneyAccounts)
    .where(and(eq(moneyAccounts.userId, userId), eq(moneyAccounts.id, id)))
    .limit(1);
  return rows[0] ? toAccount(rows[0]) : null;
}

/** A ledger row as Accounts screens show it. */
export interface AccountEntry extends AccountEntryLike {
  categoryId: string | null;
  method: string | null;
  fundId: string | null;
  createdAt: string;
}

/**
 * Every live ledger row that moves an account (accountId or toAccountId set). Balances need all
 * of them since each account's opening date, not just a window of days.
 */
export async function listAccountEntries(db: LocalDb, userId: string): Promise<AccountEntry[]> {
  const rows = await db
    .select()
    .from(ledgerEntries)
    .where(and(eq(ledgerEntries.userId, userId), isNull(ledgerEntries.deletedAt)));
  return rows
    .filter((r) => r.accountId != null || r.toAccountId != null)
    .map((r) => ({
      id: r.id,
      date: r.date,
      amountMinor: r.amountMinor,
      kind: r.kind,
      sourceModule: r.sourceModule,
      sourceId: r.sourceId ?? null,
      note: r.note ?? null,
      deletedAt: r.deletedAt ?? null,
      accountId: r.accountId ?? null,
      toAccountId: r.toAccountId ?? null,
      categoryId: r.categoryId ?? null,
      method: r.method ?? null,
      fundId: r.fundId ?? null,
      createdAt: r.createdAt,
    }));
}

/** A ledger row by id (live or deleted), as Accounts reads it, or null. */
export async function getAccountEntry(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<(AccountEntry & { deletedAt: string | null }) | null> {
  const r = (
    await db
      .select()
      .from(ledgerEntries)
      .where(and(eq(ledgerEntries.userId, userId), eq(ledgerEntries.id, id)))
      .limit(1)
  )[0];
  if (!r) return null;
  return {
    id: r.id,
    date: r.date,
    amountMinor: r.amountMinor,
    kind: r.kind,
    sourceModule: r.sourceModule,
    sourceId: r.sourceId ?? null,
    note: r.note ?? null,
    deletedAt: r.deletedAt ?? null,
    accountId: r.accountId ?? null,
    toAccountId: r.toAccountId ?? null,
    categoryId: r.categoryId ?? null,
    method: r.method ?? null,
    fundId: r.fundId ?? null,
    createdAt: r.createdAt,
  };
}

/** Fee rows a transfer wrote (sourceId = the transfer), live or deleted. */
export async function listTransferFeeIds(
  db: LocalDb,
  userId: string,
  transferId: string,
): Promise<string[]> {
  const rows = await db
    .select({ id: ledgerEntries.id, kind: ledgerEntries.kind, module: ledgerEntries.sourceModule })
    .from(ledgerEntries)
    .where(and(eq(ledgerEntries.userId, userId), eq(ledgerEntries.sourceId, transferId)));
  return rows.filter((r) => r.kind === 'expense' && r.module === 'budget').map((r) => r.id);
}

/** Live expected money, by expected day. */
export async function listExpected(db: LocalDb, userId: string): Promise<ExpectedMoneyLike[]> {
  const rows = await db
    .select()
    .from(expectedMoney)
    .where(and(eq(expectedMoney.userId, userId), isNull(expectedMoney.deletedAt)))
    .orderBy(asc(expectedMoney.expectedDate), asc(expectedMoney.id));
  const receipts = await listExpectedReceipts(db, userId);
  return rows.map((r) => toExpected(r, receipts));
}

/** v0.7.4: live receipts of repeating Coming in items (received or skipped occurrences). */
export async function listExpectedReceipts(
  db: LocalDb,
  userId: string,
  expectedId?: string,
): Promise<ExpectedReceiptLike[]> {
  const conds = [eq(expectedReceipts.userId, userId), isNull(expectedReceipts.deletedAt)];
  if (expectedId) conds.push(eq(expectedReceipts.expectedId, expectedId));
  const rows = await db
    .select()
    .from(expectedReceipts)
    .where(and(...conds));
  return rows.map(toExpectedReceipt);
}

// ---------- v0.6 Owed to you ----------

type ReceivableRow = typeof receivables.$inferSelect;

function toReceivable(r: ReceivableRow): ReceivableLike {
  return {
    id: r.id,
    name: r.name,
    amountMinor: r.amountMinor,
    expectedDate: r.expectedDate ?? null,
    note: r.note ?? null,
    createdAt: r.createdAt,
    deletedAt: r.deletedAt ?? null,
  };
}

/** Live receivables (order is worked out by receivableStatuses). */
export async function listReceivables(db: LocalDb, userId: string): Promise<ReceivableLike[]> {
  const rows = await db
    .select()
    .from(receivables)
    .where(and(eq(receivables.userId, userId), isNull(receivables.deletedAt)));
  return rows.map(toReceivable);
}

/** One receivable, including a soft-deleted one. */
export async function getReceivable(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<ReceivableLike | null> {
  const rows = await db
    .select()
    .from(receivables)
    .where(and(eq(receivables.userId, userId), eq(receivables.id, id)))
    .limit(1);
  return rows[0] ? toReceivable(rows[0]) : null;
}

/** A receipt as the Owed-to-you screen shows it. */
export interface ReceiptEntry extends ReceiptEntryLike {
  createdAt: string;
}

/**
 * Budget's deposit rows (transfers with a sourceId), deleted ones too when asked: receivable
 * receipts are among them (receivableStatus picks a receivable's own).
 */
export async function listBudgetDeposits(
  db: LocalDb,
  userId: string,
  opts: { includeDeleted?: boolean; sourceId?: string } = {},
): Promise<ReceiptEntry[]> {
  const conds = [
    eq(ledgerEntries.userId, userId),
    eq(ledgerEntries.sourceModule, 'budget'),
    eq(ledgerEntries.kind, 'transfer'),
  ];
  if (!opts.includeDeleted) conds.push(isNull(ledgerEntries.deletedAt));
  if (opts.sourceId) conds.push(eq(ledgerEntries.sourceId, opts.sourceId));
  const rows = await db
    .select()
    .from(ledgerEntries)
    .where(and(...conds));
  return rows
    .filter((r) => r.sourceId != null)
    .map((r) => ({
      id: r.id,
      date: r.date,
      amountMinor: r.amountMinor,
      kind: r.kind,
      sourceModule: r.sourceModule,
      sourceId: r.sourceId ?? null,
      toAccountId: r.toAccountId ?? null,
      deletedAt: r.deletedAt ?? null,
      createdAt: r.createdAt,
    }));
}

/** One expected item, including a soft-deleted one. */
export async function getExpected(
  db: LocalDb,
  userId: string,
  id: string,
): Promise<ExpectedMoneyLike | null> {
  const rows = await db
    .select()
    .from(expectedMoney)
    .where(and(eq(expectedMoney.userId, userId), eq(expectedMoney.id, id)))
    .limit(1);
  return rows[0] ? toExpected(rows[0], await listExpectedReceipts(db, userId, id)) : null;
}
